class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (
          (event.source === "cart-items" &&
            event.cartSurface === this.tagName) || event.source === "cart-drawer-sync"
        ) {
          return;
        }
        return this.onCartUpdate(
          event.renderEpoch,
          event.cartMutationClaimed === true,
        );
      },
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = "";

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace(
        "[min]",
        event.target.dataset.min,
      );
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace(
        "[max]",
        event.target.max,
      );
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        "[step]",
        event.target.step,
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute("name"),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate(renderEpoch, cartMutationClaimed = false) {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      const cartDrawer = document.querySelector("cart-drawer");
      const requestEpoch =
        renderEpoch === undefined
          ? cartDrawer?.captureRenderEpoch?.()
          : renderEpoch;
      if (cartDrawer?.cartRefreshInFlight) {
        if (
          !cartMutationClaimed &&
          typeof cartDrawer.claimCartMutationRender === "function"
        ) {
          if (cartDrawer.claimCartMutationRender(requestEpoch) === undefined)
            cartDrawer.requestCartRefresh?.();
        } else {
          cartDrawer.requestCartRefresh?.();
        }
        return Promise.resolve();
      }
      if (
        typeof cartDrawer?.isRenderEpochCurrent === "function" &&
        !cartDrawer.isRenderEpochCurrent(requestEpoch)
      ) {
        cartDrawer.requestCartRefresh?.();
        return Promise.resolve();
      }
      // The refresh is a cart read followed by a DOM swap; hold the Try Before You Buy write lock
      // across it so a booking cannot land between the fetch and the swap of stale markup.
      globalThis.UMITbyb?.cartMutationStart();
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const claimedRenderEpoch =
            typeof cartDrawer?.claimCartMutationRender === "function"
              ? cartDrawer.claimCartMutationRender(requestEpoch)
              : typeof cartDrawer?.isRenderEpochCurrent !== "function" ||
                  cartDrawer.isRenderEpochCurrent(requestEpoch)
                ? requestEpoch
                : undefined;
          if (claimedRenderEpoch === undefined) {
            if (!cartDrawer?.didCartRefreshSupersede?.(requestEpoch))
              cartDrawer.requestCartRefresh?.();
            return;
          }

          const html = new DOMParser().parseFromString(
            responseText,
            "text/html",
          );
          // The whole footer, not just the totals block: the Try Before You Buy tabs, reason and
          // CTA state live around the totals and must refresh with the lines. The free-shipping
          // bar sits above the lines and follows the same total.
          const selectors = [
            "#CartFreeShipping-drawer",
            "cart-drawer-items",
            ".drawer__footer",
          ];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          // The checkout button is the focus trap's last stop; it was just replaced.
          const drawer = document.querySelector("cart-drawer.active");
          if (drawer && typeof trapFocus === "function") {
            const container = document.getElementById("CartDrawer");
            const active = document.activeElement;
            trapFocus(
              container,
              active && container.contains(active)
                ? active
                : drawer.querySelector(".drawer__inner"),
            );
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          globalThis.UMITbyb?.cartMutationEnd();
        });
    } else {
      // Sections must be requested by their template id: rendering `main-cart-footer` by file
      // name returns it with default settings and no blocks — no totals, no checkout button.
      const itemsId = document.getElementById("main-cart-items")?.dataset.id;
      const footerId = document.getElementById("main-cart-footer")?.dataset.id;
      const ids = [itemsId, footerId].filter(Boolean);
      if (ids.length === 0) return Promise.resolve();
      globalThis.UMITbyb?.cartMutationStart();
      return fetch(`${routes.cart_url}?sections=${ids.join(",")}`)
        .then((response) => response.json())
        .then((sections) => {
          if (itemsId && sections[itemsId]) {
            const itemsHtml = new DOMParser().parseFromString(
              sections[itemsId],
              "text/html",
            );
            const sourceItems = itemsHtml.querySelector("cart-items");
            if (sourceItems) {
              this.innerHTML = sourceItems.innerHTML;
              // A cart emptied elsewhere (the drawer) must leave the page's two-column layout.
              this.classList.toggle(
                "is-empty",
                sourceItems.classList.contains("is-empty"),
              );
            }
          }

          const footerTarget = document.querySelector(
            "#main-cart-footer .cart__blocks",
          );
          if (footerTarget && footerId && sections[footerId]) {
            const footerHtml = new DOMParser().parseFromString(
              sections[footerId],
              "text/html",
            );
            const footerSource = footerHtml.querySelector(".cart__blocks");
            if (footerSource) footerTarget.innerHTML = footerSource.innerHTML;
            const footerState = footerHtml.getElementById("main-cart-footer");
            if (footerState) {
              document
                .getElementById("main-cart-footer")
                .classList.toggle(
                  "is-empty",
                  footerState.classList.contains("is-empty"),
                );
            }
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          globalThis.UMITbyb?.cartMutationEnd();
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".cart__blocks",
      },
    ];
  }

  // `key` targets the line by its line key instead of its position: a size change adds the new
  // variant first, which Shopify puts at the top and so shifts every index. With a key the
  // promise rejects on a failed change, so the caller can redraw and retry.
  updateQuantity(line, quantity, event, name, variantId, key) {
    globalThis.UMITbyb?.cartMutationStart();
    this.enableLoading(line);
    const cartDrawer = document.querySelector("cart-drawer");
    const renderEpoch = cartDrawer?.captureRenderEpoch?.();

    const body = JSON.stringify({
      ...(key ? { id: key } : { line }),
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget =
      event?.currentTarget instanceof CartRemoveButton ? "clear" : "change";

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        // Shopify answers a change it refuses (a stale key: 400, stock: 422) with no sections.
        if (key && parsedState.status) {
          throw Object.assign(
            new Error(parsedState.description || parsedState.message || ""),
            { status: parsedState.status },
          );
        }
        const claimedRenderEpoch =
          typeof cartDrawer?.claimCartMutationRender === "function"
            ? cartDrawer.claimCartMutationRender(renderEpoch)
            : typeof cartDrawer?.isRenderEpochCurrent !== "function" ||
                cartDrawer.isRenderEpochCurrent(renderEpoch)
              ? renderEpoch
              : undefined;
        if (claimedRenderEpoch === undefined) {
          cartDrawer.requestCartRefresh?.();
          return;
        }

        CartPerformance.measure(
          `${eventTarget}:paint-updated-sections"`,
          () => {
            const quantityElement =
              document.getElementById(`Quantity-${line}`) ||
              document.getElementById(`Drawer-quantity-${line}`);
            const items = document.querySelectorAll(".cart-item");

            if (parsedState.errors) {
              quantityElement.value = quantityElement.getAttribute("value");
              this.updateLiveRegions(line, parsedState.errors);
              return;
            }

            this.classList.toggle("is-empty", parsedState.item_count === 0);
            const cartDrawerWrapper = document.querySelector("cart-drawer");
            const cartFooter = document.getElementById("main-cart-footer");

            if (cartFooter)
              cartFooter.classList.toggle(
                "is-empty",
                parsedState.item_count === 0,
              );
            if (cartDrawerWrapper)
              cartDrawerWrapper.classList.toggle(
                "is-empty",
                parsedState.item_count === 0,
              );

            this.getSectionsToRender().forEach((section) => {
              const elementToReplace =
                document
                  .getElementById(section.id)
                  .querySelector(section.selector) ||
                document.getElementById(section.id);
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector,
              );
            });
            const updatedValue = parsedState.items[line - 1]
              ? parsedState.items[line - 1].quantity
              : undefined;
            let message = "";
            if (
              !key &&
              items.length === parsedState.items.length &&
              updatedValue !== parseInt(quantityElement.value)
            ) {
              if (typeof updatedValue === "undefined") {
                message = window.cartStrings.error;
              } else {
                message = window.cartStrings.quantityError.replace(
                  "[quantity]",
                  updatedValue,
                );
              }
            }
            this.updateLiveRegions(line, message);

            const lineItem =
              document.getElementById(`CartItem-${line}`) ||
              document.getElementById(`CartDrawer-Item-${line}`);
            if (key) {
              // The caller puts focus back on the line it changed; the open drawer's trap must
              // learn its new first and last stops.
              if (cartDrawerWrapper?.classList.contains("active")) {
                cartDrawerWrapper.refreshFocusTrap?.();
              }
            } else if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
              cartDrawerWrapper
                ? trapFocus(
                    cartDrawerWrapper,
                    lineItem.querySelector(`[name="${name}"]`),
                  )
                : lineItem.querySelector(`[name="${name}"]`).focus();
            } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
              trapFocus(
                cartDrawerWrapper.querySelector(".drawer__inner-empty"),
                cartDrawerWrapper.querySelector("a"),
              );
            } else if (
              document.querySelector(".cart-item") &&
              cartDrawerWrapper
            ) {
              trapFocus(
                cartDrawerWrapper,
                document.querySelector(".cart-item__name"),
              );
            }
          },
        );

        if (event) CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartSurface: this.tagName,
          cartData: parsedState,
          variantId: variantId,
          renderEpoch: claimedRenderEpoch,
          cartMutationClaimed: true,
        });
      })
      .catch((error) => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden"),
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        if (errors) errors.textContent = window.cartStrings.error;
        if (key) throw error;
      })
      .finally(() => {
        this.disableLoading(line);
        globalThis.UMITbyb?.cartMutationEnd();
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").textContent =
        message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`,
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`,
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden"),
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`,
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`,
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden"),
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            }).then(() =>
              CartPerformance.measureFromEvent(
                "note-update:user-action",
                event,
              ),
            );
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
